import { roundedLgClassname } from '@/helpers/environmentHelper';
import { useAppSelector } from '@/state/hooks';
import { LiteMarket, selectLiteMarkets } from '@/state/reducers/liteMarketSlice';
import useContextMenu from '@/utils/hooks/useContextMenu';
import { getCurrency, useCurrency } from '@/utils/hooks/useCurrency';
import { useDidUpdate } from '@/utils/hooks/useDidUpdate';
import { useDisclosure } from '@/utils/hooks/useDisclosure';
import { GetColorIndexesReturn, useInstrument } from '@/utils/hooks/useInstrument';
import useLanguage from '@/utils/hooks/useLanguage';
import usePreviousSelector from '@/utils/hooks/usePreviousSelector';
import BigNumber from 'bignumber.js';
import cn from 'classnames';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { MdAddCircle, MdRemoveCircle, MdSettings } from 'react-icons/md';
import ContextMenu, { ContextMenuItem } from '../common/ContextMenu';
import CurrencyIcon from '../common/CurrencyIcon';
import LiteMarketsModal from '../modal/LiteMarketsModal';
import LiteOrderModal from '../modal/LiteOrderModal';
import { Highlights } from '../modules/MarketList/UserMarket';

const LiteMarkets = ({ currencyConfigDict }: any) => {
    const marketModal = useDisclosure(false);
    const convertModal = useDisclosure(false);
    const tradeMarkets = useAppSelector(selectLiteMarkets);

    const lang = useLanguage();

    const [selectedMarket, setSelectedMarket] = useState<LiteMarket>({} as LiteMarket);
    const [initFrom, setInitFrom] = useState<string>('');
    const [initTo, setInitTo] = useState<string>('');

    const [showConvertFrom, showConvertTo] = useMemo(() => {
        const showConvertFrom = getCurrency(selectedMarket?.name);
        const showConvertTo = getCurrency(selectedMarket?.toName);

        return [showConvertFrom.show, showConvertTo.show];
    }, [selectedMarket]);

    const { contextMenuX, contextMenuY, contextMenuVisible, handleOpenContextMenu, handleCloseContextMenu } =
        useContextMenu();

    const onSelectingBalance = useCallback(
        (e: any, market: LiteMarket) => {
            setSelectedMarket({
                ...market,
                name: getCurrency(market.name).show,
                toName: getCurrency(market.toName).show
            });
            handleOpenContextMenu(e);
        },
        [handleOpenContextMenu]
    );

    return (
        <Fragment>
            <div
                className={cn(
                    'flex flex-col h-fit w-full md:w-3/5 gap-2 sm:gap-3 sm:bg-brand-background-dark p-0 sm:p-6',
                    roundedLgClassname
                )}>
                <div className="flex justify-between items-center">
                    <span className="text-base sm:text-lg font-bold px-2 sm:p-0">{lang.lite.todaysMarket}</span>
                    <div className="p-1 rounded-full cursor-pointer hover:bg-brand-accent-light">
                        <MdSettings className="w-5 h-5" onClick={marketModal[1].open} />
                    </div>
                </div>
                <div className="flex flex-col gap-3">
                    {tradeMarkets.map((market, index) => (
                        <LiteMarketMonitor key={index} market={market} onClick={onSelectingBalance} />
                    ))}
                </div>
            </div>
            <ContextMenu
                x={contextMenuX}
                y={contextMenuY}
                visible={contextMenuVisible}
                onClose={handleCloseContextMenu}>
                <ContextMenuItem
                    onClick={() => {
                        setInitFrom(getCurrency(selectedMarket.toName).show);
                        setInitTo(getCurrency(selectedMarket.name).show);
                        convertModal[1].toggle();
                    }}>
                    <MdAddCircle />
                    <span>
                        {lang.orderForm.buy} {showConvertFrom}, {lang.orderForm.sell} {showConvertTo}
                    </span>
                </ContextMenuItem>
                <ContextMenuItem
                    className="hover:bg-brand-red"
                    onClick={() => {
                        setInitFrom(getCurrency(selectedMarket.name).show);
                        setInitTo(getCurrency(selectedMarket.toName).show);
                        convertModal[1].toggle();
                    }}>
                    <MdRemoveCircle />
                    <span>
                        {lang.orderForm.sell} {showConvertFrom}, {lang.orderForm.buy} {showConvertTo}
                    </span>
                </ContextMenuItem>
            </ContextMenu>
            <LiteMarketsModal opened={marketModal[0]} handlers={marketModal[1]} />
            <LiteOrderModal initFrom={initFrom} initTo={initTo} opened={convertModal[0]} handlers={convertModal[1]} />
        </Fragment>
    );
};

export default LiteMarkets;

interface LiteMarketMonitorProps {
    market: LiteMarket;
    onClick: (e: any, market: LiteMarket) => void;
}

const LiteMarketMonitor = ({ market, onClick }: LiteMarketMonitorProps) => {
    const linear = `${market.celer}/${market.toCeler}`;
    const invert = `${market.toCeler}/${market.celer}`;

    const { instrumentDirection, formatPip, formatHighlights } = useInstrument(linear, invert);
    const instrument = instrumentDirection === 'linear' ? linear : invert;
    const bidAskType = instrumentDirection === 'linear' ? 'ask' : 'bid';

    const bidAsk = useAppSelector((state) => state.marketPair.bidAsk[instrument]);
    const prevBid = usePreviousSelector(
        (state) => state.marketPair.bidAsk[instrument] && state.marketPair.bidAsk[instrument][bidAskType]
    ) as number;

    const rate = convertToRate(bidAsk, bidAskType);

    const { longName, show } = useCurrency(market.name);
    const [bidHighlights, setBidHighlights] = useState<GetColorIndexesReturn | undefined>();

    const marketPrice = useMemo(() => rate && formatPip(rate), [rate]);

    // for highlighting changes based on prev price
    useDidUpdate(() => {
        if (bidAsk && rate && prevBid) setBidHighlights(formatHighlights(prevBid, rate, bidHighlights));
    }, [rate]);

    return (
        <div
            className="flex justify-between w-full gap-3 p-1 px-3 rounded-md text-xs sm:text-sm hover:bg-brand-neutral-700 focus:bg-brand-primary-light cursor-pointer"
            onClick={(e) => {
                if (marketPrice) {
                    onClick(e, market);
                }
            }}>
            <div className="flex items-center gap-2">
                <CurrencyIcon ccy={market.logo} />
                <div className="flex flex-col">
                    <span>{longName || market.name}</span>
                    <span className="text-brand-neutral-400 text-2xs sm:text-xs">{show}</span>
                </div>
            </div>
            {marketPrice !== 0 && rate !== 0 && (
                <div className="flex items-center gap-2">
                    <span>{market.toName}</span>
                    <div className="flex-1 flex items-center justify-end">
                        <Highlights ticker={marketPrice} highlights={bidHighlights} />
                    </div>
                </div>
            )}
        </div>
    );
};

function convertToRate(bidAsk: { bid: number; ask: number } | undefined, type: string) {
    if (bidAsk?.ask && type === 'ask') return BigNumber(bidAsk.ask).toNumber();
    else if (bidAsk?.bid && type === 'bid') return BigNumber(bidAsk.bid).toNumber();
    else return 0;
}
